import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import styled from 'styled-components';
import {
  Card, StatGrid, Stat, StatTop, StatIco, StatLabel, StatValue,
  Tag, TagDot, Chip, Muted,
} from '../ui/primitives';
import {
  StatValueShimmer,
  DomainsPanelShimmer,
} from './assessmentDetailLoadingShimmer';
import PageActionsMenu, {
  ActionsDivider,
  ActionsMenuItem,
  ActionsSection,
} from '../ui/PageActionsMenu';
import {
  Sparkles,
  ClipboardList,
  UserPlus,
  Pencil,
  Settings,
  ToggleLeft,
  ToggleRight,
  FileText,
  Trash2,
} from 'lucide-react';
import { Icon } from '../ui/shell';
import { LabelWithHelp } from '../ui/FieldHelp';
import { ASSESSMENT_DETAIL_KPI_HELP } from '../ui/kpiFieldHelp';
import FormDialog from './dialog';
import Assignstudentdialog from './assignstudentdialog';
import GenerateQuestiondialog from './GenerateQuestiondialog';
import ReportDialog from './ReportDialog';
import DeleteConfirmDialog from '../ui/DeleteConfirmDialog';
import AssessmentSettingDialog from './AssessmentSettingDialog';
import { useConfirm } from '../ui/ConfirmProvider';
import { getCache, setCache, clearCache, clearCacheByPrefix } from '../ui/listCache';
import {
  buildQuestionListCacheKey,
  buildQuestionListQuery,
  parseQuestionListResponse,
} from './questionListApi';
import { useAppContext } from '../../appprovider';
import {
  canUseAiCourseBuilder,
  aiCourseBuilderGateBody,
} from '../services/instructorMeterLimits';
import useDemoActionGate from '../hooks/useDemoActionGate';
import DemoLockedDialog from '../ui/DemoLockedDialog';

/* ============================================================
   Assessment detail workspace — opens when an assessment row is
   clicked (from the Assessments list or a course's Assessments
   tab). Mirrors CourseDetail: back button + hero + KPI cards + a
   domains/subdomains overview (fetched from the same endpoint the
   assessment edit dialog uses), with every assessment action as a
   button. Domain data is cached per assessment so a Back navigation
   restores it instantly — it's only refetched when the assessment
   is updated or its questions are regenerated.
   ============================================================ */

const Shell = styled.div`
  min-height: calc(100vh - 32px);
  margin: 0 auto;
  padding: 0 28px 36px;
  color: ${({ theme }) => theme.text};
  font-family: ${({ theme }) => theme.fontBody};
  @media (max-width: 768px) { padding: 0 14px 28px; }
`;

const BackBar = styled.div`
  width: min(1180px, 100%);
  margin: ${({ theme }) => theme.gap} auto 0;
`;

const BackBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 32px;
  padding: 0 12px 0 8px;
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: ${({ theme }) => theme.rMd};
  background: ${({ theme }) => theme.surface};
  color: ${({ theme }) => theme.text};
  font-family: inherit;
  font-size: ${({ theme }) => theme.fsSm};
  font-weight: 650;
  cursor: pointer;
  &:hover { background: ${({ theme }) => theme.surface2}; }
`;

const Hero = styled(Card)`
  width: min(1180px, 100%);
  margin: 10px auto ${({ theme }) => theme.gap};
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
  flex-wrap: wrap;
  padding: 18px 20px;

  .hero__title { display: flex; align-items: center; gap: 10px; font-family: ${({ theme }) => theme.fontHead}; font-size: 18px; font-weight: 780; color: ${({ theme }) => theme.text}; }
  .hero__sub { margin-top: 4px; color: ${({ theme }) => theme.textMuted}; font-size: ${({ theme }) => theme.fsSm}; }
  .hero__chips { margin-top: 10px; display: flex; gap: 6px; flex-wrap: wrap; }
  .hero__main { flex: 1 1 280px; min-width: 0; }
`;

/** Hero toolbar — primary + Actions sheet. */
const HeroActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 10px;
  flex: 1 1 280px;
  min-width: 0;
  max-width: 100%;
`;

const Wrap = styled.div`
  width: min(1180px, 100%);
  margin: 16px auto 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.gap};
`;

const SectionTitle = styled.h3`
  margin: 0 0 10px;
  font-size: 14px;
  font-weight: 750;
  color: ${({ theme }) => theme.text};
`;

const DomainRow = styled.div`
  padding: 9px 0;
  & + & { border-top: 1px solid ${({ theme }) => theme.border}; }
  .dom__head { display: flex; align-items: center; justify-content: space-between; gap: 8px; font-size: 13px; font-weight: 650; color: ${({ theme }) => theme.text}; }
  .dom__pct { font-size: 11px; font-weight: 700; color: ${({ theme }) => theme.accentStrong}; }
  ul { margin: 6px 0 0; padding-left: 16px; font-size: 11.5px; line-height: 1.5; color: ${({ theme }) => theme.textMuted}; }
`;

const authHeaders = () => {
  const u = process.env.REACT_APP_API_USERNAME;
  const p = process.env.REACT_APP_API_PASSWORD;
  return {
    'Content-Type': 'application/json',
    Accept: '*/*',
    'Access-Control-Allow-Origin': '*',
    Authorization: `Basic ${btoa(`${u}:${p}`)}`,
  };
};

const domainsKey = (testId) => `asmt-domains:${testId}`;
const questionsKey = (testId) => `questions:${testId}`;
const questionCountCacheKey = (testId) => buildQuestionListCacheKey(testId, { page: 0, size: 1 });

export default function AssessmentDetail() {
  const location = useLocation();
  const navigate = useNavigate();
  const askConfirmation = useConfirm();
  const apiUrl = process.env.REACT_APP_API_URL;
  const { entitlements, openUpgradeGate } = useAppContext();
  const { isLocked, guardAction, lockProps, lockedDialogOpen, setLockedDialogOpen } = useDemoActionGate();
  const aiBuilderEnabled = canUseAiCourseBuilder(entitlements);

  const openGenerateDialog = () => {
    if (!guardAction('action.assessment.detail.generate')) return;
    if (!aiBuilderEnabled) {
      openUpgradeGate(aiCourseBuilderGateBody(entitlements));
      return;
    }
    setGenOpen(true);
  };

  const passed = (location.state && (location.state.assessment || location.state.testDetails)) || {};
  const [assessment, setAssessment] = useState(passed);
  const [domains, setDomains] = useState([]);
  const [domainsLoading, setDomainsLoading] = useState(false);
  const [questionCount, setQuestionCount] = useState(null);
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [loading, setLoading] = useState(false);

  const testId = assessment.testId || passed.testId;

  // dialog state
  const [genOpen, setGenOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [updateOpen, setUpdateOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsAssessment, setSettingsAssessment] = useState(null);

  const goBack = () => navigate(-1);

  const handleOpenSettings = () => {
    if (!guardAction('action.assessment.detail.settings')) return;
    setSettingsAssessment(assessment);
    setSettingsOpen(true);
  };

  const handleCloseSettings = () => {
    setSettingsOpen(false);
    setSettingsAssessment(null);
  };

  // Fetch domains/subdomains from the SAME endpoint the assessment edit
  // dialog uses, and cache them by assessment id.
  const fetchDomains = useCallback(async () => {
    if (!testId) return;
    setDomainsLoading(true);
    try {
      const res = await axios.get(`${apiUrl}/api/AssessmentDomain/${testId}`, { headers: authHeaders() });
      const data = Array.isArray(res.data) ? res.data : [];
      setDomains(data);
      setCache(domainsKey(testId), data);
    } catch (err) {
      /* non-fatal — overview just shows no domains */
    } finally {
      setDomainsLoading(false);
    }
  }, [testId, apiUrl]);

  // Cache-first: instant restore on Back; only fetch when not cached.
  useEffect(() => {
    if (!testId) return;
    const cached = getCache(domainsKey(testId));
    if (cached) setDomains(cached);
    else fetchDomains();
  }, [testId, fetchDomains]);

  const fetchQuestionCount = useCallback(async () => {
    if (!testId) return;
    setQuestionsLoading(true);
    try {
      const cacheKey = questionCountCacheKey(testId);
      const cached = getCache(cacheKey);
      if (cached != null) {
        setQuestionCount(cached);
        return;
      }
      const query = buildQuestionListQuery({ testId, page: 0, size: 1 });
      const res = await axios.get(`${apiUrl}/api/by-test?${query}`, { headers: authHeaders() });
      const parsed = parseQuestionListResponse(res.data);
      const count = parsed.totalElements;
      setQuestionCount(count);
      setCache(cacheKey, count);
    } catch (err) {
      /* non-fatal — KPI falls back to passed assessment count */
    } finally {
      setQuestionsLoading(false);
    }
  }, [testId, apiUrl]);

  useEffect(() => {
    if (!testId) return;
    fetchQuestionCount();
  }, [testId, fetchQuestionCount]);

  const isActive = assessment.active === 1;

  const handleEnabled = async () => {
    if (!guardAction('action.assessment.detail.disable')) return;
    const action = assessment.active === 1 ? 'disable' : 'enable';
    const actionLabel = action.charAt(0).toUpperCase() + action.slice(1);
    const confirmed = await askConfirmation({
      title: `${actionLabel} assessment?`,
      message: `Are you sure you want to ${action} this assessment?`,
      confirmLabel: actionLabel,
    });
    if (!confirmed) return;

    try {
      const active = assessment.active === 1 ? 0 : 1;
      await axios.put(`${apiUrl}/api/update-assessment-active-status`, { testId, active }, { headers: authHeaders() });
      setAssessment((a) => ({ ...a, active }));
    } catch (err) {
      console.error('Toggle status failed:', err);
    }
  };

  const doDelete = async () => {
    if (!guardAction('action.assessment.detail.delete')) return;
    try {
      await fetch(`${apiUrl}/api/Assessment/deleteTest/${testId}`, { method: 'POST', headers: authHeaders() });
      clearCache(domainsKey(testId));
      clearCache(questionsKey(testId));
      navigate(-1);
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  const handleReport = async () => {
    if (!testId) return;
    if (!guardAction('action.assessment.detail.resource')) return;
    try {
      setLoading(true);
      const res = await axios.get(`${apiUrl}/api/report-per-test/${testId}`, { headers: authHeaders() });
      setReportData(res.data);
      setReportOpen(true);
    } catch (err) {
      console.error('Report fetch failed:', err);
    } finally {
      setLoading(false);
    }
  };

  // "Show questions" opens the full Questions grid (questionsGrid.js) — not a
  // dialog — so the user gets the same management view, with a Back button.
  const showQuestions = () => {
    guardAction('action.assessment.detail.showQuestions', () => {
      navigate('/Portal/Instructor/Questions', { state: { testId, fromLabel: assessment.name } });
    });
  };

  // After an update, the assessment's domains (and its question set) may change,
  // so invalidate both caches and refetch the overview.
  const closeUpdate = () => {
    setUpdateOpen(false);
    clearCache(domainsKey(testId));
    clearCache(questionsKey(testId));
    clearCacheByPrefix(`questions:${testId}:`);
    fetchDomains();
    fetchQuestionCount();
  };

  // New questions were just generated -> the cached question list is stale.
  const closeGenerate = () => {
    setGenOpen(false);
    clearCache(questionsKey(testId));
    clearCacheByPrefix(`questions:${testId}:`);
    fetchQuestionCount();
  };

  const resolvedQuestionCount = questionCount ?? assessment.questions ?? assessment.noOfQuestions;

  const kpis = [
    {
      label: 'Questions',
      value: resolvedQuestionCount ?? '—',
      loading: questionsLoading,
      icon: 'assess',
      help: ASSESSMENT_DETAIL_KPI_HELP.questions,
    },
    { label: 'Duration', value: assessment.examTime ? `${assessment.examTime}m` : (assessment.duration ? `${assessment.duration}m` : '—'), icon: 'target', help: ASSESSMENT_DETAIL_KPI_HELP.duration },
    { label: 'Total marks', value: assessment.totalMarks ?? assessment.total_marks ?? '—', icon: 'award', help: ASSESSMENT_DETAIL_KPI_HELP.totalMarks },
    {
      label: 'Domains',
      value: domains.length,
      loading: domainsLoading,
      icon: 'enroll',
      help: ASSESSMENT_DETAIL_KPI_HELP.domains,
    },
  ];

  // Guard: opened without an assessment in navigation state.
  if (!testId) {
    return (
      <Shell>
        <BackBar><BackBtn onClick={goBack}><Icon name="chevL" size={16} />Back</BackBtn></BackBar>
        <Wrap>
          <Card $pad>
            <Muted as="p" style={{ margin: 0, fontSize: 13 }}>
              This assessment couldn't be loaded. Please go back and open it again.
            </Muted>
          </Card>
        </Wrap>
      </Shell>
    );
  }

  return (
    <Shell>
      <BackBar><BackBtn onClick={goBack}><Icon name="chevL" size={16} />Back</BackBtn></BackBar>

      <Hero>
        <div className="hero__main">
          <div className="hero__title">
            {assessment.name || 'Assessment'}
            <Tag $tone={isActive ? 'green' : 'gray'}><TagDot />{isActive ? 'Active' : 'Disabled'}</Tag>
          </div>
          <div className="hero__sub">
            {assessment.courseName || '—'} · {assessment.examCode || assessment.exam_code || '—'}
          </div>
          <div className="hero__chips">
            {assessment.vendor && <Chip $soft>{assessment.vendor}</Chip>}
            {assessment.type && <Chip $soft>{assessment.type}</Chip>}
            {assessment.grade && <Chip $soft>{assessment.grade}</Chip>}
          </div>
        </div>

        <HeroActions>
          {aiBuilderEnabled && (
          <PageActionsMenu
            primaryLabel="Generate questions"
            primaryIcon={<Sparkles size={16} strokeWidth={2.25} aria-hidden="true" />}
            onPrimaryClick={openGenerateDialog}
            primaryLocked={isLocked('action.assessment.detail.generate')}
            menuTitle="Assessment actions"
            menuSubtitle="Review, assign, and manage this assessment."
          >
            {(closeActions) => (
              <>
                <ActionsSection label="Workflows">
                  <ActionsMenuItem
                    label="Show questions"
                    description="Review and edit the question bank"
                    icon={<ClipboardList size={15} strokeWidth={1.9} />}
                    closeActions={closeActions}
                    onClick={showQuestions}
                    {...lockProps('action.assessment.detail.showQuestions')}
                  />
                  <ActionsMenuItem
                    label="Assign student"
                    description="Grant access to enrolled learners"
                    icon={<UserPlus size={15} strokeWidth={1.9} />}
                    closeActions={closeActions}
                    onClick={() => guardAction('action.assessment.detail.assign', () => setAssignOpen(true))}
                    {...lockProps('action.assessment.detail.assign')}
                  />
                </ActionsSection>
                <ActionsSection label="Manage">
                  <ActionsMenuItem
                    label="Update assessment"
                    description="Edit name, dates, and metadata"
                    icon={<Pencil size={15} strokeWidth={1.9} />}
                    closeActions={closeActions}
                    onClick={() => guardAction('action.assessment.detail.update', () => setUpdateOpen(true))}
                    {...lockProps('action.assessment.detail.update')}
                  />
                  <ActionsMenuItem
                    label="Assessment settings"
                    description="Timing, scoring, and delivery options"
                    icon={<Settings size={15} strokeWidth={1.9} />}
                    closeActions={closeActions}
                    onClick={handleOpenSettings}
                    {...lockProps('action.assessment.detail.settings')}
                  />
                  <ActionsMenuItem
                    label={isActive ? 'Disable assessment' : 'Enable assessment'}
                    description={isActive ? 'Hide from learners' : 'Make available to learners'}
                    icon={isActive
                      ? <ToggleLeft size={15} strokeWidth={1.9} />
                      : <ToggleRight size={15} strokeWidth={1.9} />}
                    closeActions={closeActions}
                    onClick={handleEnabled}
                    {...lockProps('action.assessment.detail.disable')}
                  />
                  <ActionsMenuItem
                    label="Instructor resource"
                    description="Open printable exam resource"
                    icon={<FileText size={15} strokeWidth={1.9} />}
                    disabled={loading}
                    closeActions={closeActions}
                    onClick={handleReport}
                    {...lockProps('action.assessment.detail.resource')}
                  />
                </ActionsSection>
                <ActionsDivider aria-hidden="true" />
                <ActionsMenuItem
                  label="Delete assessment"
                  description="Permanently remove this assessment"
                  icon={<Trash2 size={15} strokeWidth={1.9} />}
                  danger
                  closeActions={closeActions}
                  onClick={() => guardAction('action.assessment.detail.delete', () => setConfirmOpen(true))}
                  {...lockProps('action.assessment.detail.delete')}
                />
              </>
            )}
          </PageActionsMenu>
          )}
        </HeroActions>
      </Hero>

      <StatGrid style={{ marginTop: 10, width: 'min(1180px, 100%)', marginLeft: 'auto', marginRight: 'auto' }}>
        {kpis.map((k) => (
          <Stat key={k.label}>
            <StatTop><StatIco><Icon name={k.icon} /></StatIco></StatTop>
            <StatLabel><LabelWithHelp help={k.help}>{k.label}</LabelWithHelp></StatLabel>
            <StatValue className="tnum">
              {k.loading ? (
                <span aria-label={`Loading ${k.label.toLowerCase()}`}>
                  <StatValueShimmer />
                </span>
              ) : k.value}
            </StatValue>
          </Stat>
        ))}
      </StatGrid>

      <Wrap>
        <Card $pad>
          <SectionTitle>Domains &amp; subdomains</SectionTitle>
          {domainsLoading ? (
            <DomainsPanelShimmer />
          ) : domains.length === 0 ? (
            <Muted as="p" style={{ margin: 0, fontSize: 13 }}>No domains found for this assessment.</Muted>
          ) : (
            domains.map((d, i) => (
              <DomainRow key={i}>
                <div className="dom__head">
                  <span>{d.name}</span>
                  {d.percentage != null && d.percentage !== '' && <span className="dom__pct">{d.percentage}%</span>}
                </div>
                {(d.subDomain || []).length > 0 && (
                  <ul>
                    {d.subDomain.map((s, k) => {
                      const topics = s.sub_topics || s.subTopics;
                      return <li key={k}>{s.name}{topics ? ` — ${topics}` : ''}</li>;
                    })}
                  </ul>
                )}
              </DomainRow>
            ))
          )}
        </Card>
      </Wrap>

      {/* ---- dialogs (reused from the Assessments page) ---- */}
      {genOpen && (
        <GenerateQuestiondialog open={genOpen} handleClose={closeGenerate} data={assessment} testId={testId} />
      )}
      {assignOpen && (
        <Assignstudentdialog open={assignOpen} handleClose={() => setAssignOpen(false)} data={assessment} />
      )}
      {updateOpen && (
        <FormDialog open={updateOpen} handleClose={closeUpdate} data={assessment} testId={testId} />
      )}
      <ReportDialog open={reportOpen} onClose={() => setReportOpen(false)} reportData={reportData} />
      <DeleteConfirmDialog
        open={confirmOpen}
        count={1}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => { setConfirmOpen(false); doDelete(); }}
      />
      <AssessmentSettingDialog
        open={settingsOpen}
        testDetails={settingsAssessment}
        onClose={handleCloseSettings}
        saveLocked={isLocked('action.assessment.detail.settings')}
        onSaveBlocked={() => setLockedDialogOpen(true)}
        onSaved={handleCloseSettings}
      />
      <DemoLockedDialog open={lockedDialogOpen} onClose={() => setLockedDialogOpen(false)} />
    </Shell>
  );
}
